"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TITLE_SIZE_OPTIONS = exports.TITLE_ALIGN_OPTIONS = exports.TITLE_COLOR_OPTIONS = exports.resolveTitleStyleCss = exports.resolveTitleSizeCss = exports.resolveTitleAlignCss = exports.resolveTitleColorCss = exports.resolveTitleSize = exports.resolveTitleAlign = exports.resolveTitleColor = exports.titleSizeKeySchema = exports.titleAlignKeySchema = exports.titleColorKeySchema = exports.TITLE_STYLE_DEFAULTS = exports.TITLE_FRAME_CSS = exports.TITLE_SIZES = exports.TITLE_ALIGNS = exports.TITLE_COLORS = void 0;
const zod_1 = require("zod");
/**
 * #510 — Constrained presets for urban-study content **title** styling.
 *
 * Single source of truth shared by the admin editor (Select options + Zod
 * validation), the backend PDF renderer (preset key → Typst token) and the admin
 * content **preview** (preset key → CSS token, #677). Values are the keys persisted
 * in `urban_planning_rule_path_contents.title_color/align/size`; `null`/unknown
 * resolves to the default, which reproduces the historical hardcoded title look
 * (`#align(center)[#text(size: 10pt, fill: rgb("#ff5858"))]`).
 *
 * Each preset carries BOTH tokens so the preview cannot drift from the PDF: add a
 * preset here and both renderers get it. `css` sizes are the `pt` values converted
 * at 96dpi (1pt = 4/3px), so the on-screen title keeps the printed proportions.
 */
exports.TITLE_COLORS = {
    dark_blue: { label: 'Bleu foncé', typst: 'rgb("#012060")', css: '#012060' },
    red: { label: 'Rouge', typst: 'rgb("#ff5858")', css: '#ff5858' },
    black: { label: 'Noir', typst: 'black', css: '#000000' },
    grey: { label: 'Gris', typst: 'luma(40%)', css: '#666666' },
};
exports.TITLE_ALIGNS = {
    left: { label: 'Gauche', typst: 'left', css: 'left' },
    center: { label: 'Centré', typst: 'center', css: 'center' },
    right: { label: 'Droite', typst: 'right', css: 'right' },
};
exports.TITLE_SIZES = {
    small: { label: 'Petit', typst: '9pt', css: '12px' },
    medium: { label: 'Moyen', typst: '10pt', css: '13px' },
    large: { label: 'Grand', typst: '12pt', css: '16px' },
    xlarge: { label: 'Très grand', typst: '16pt', css: '21px' },
};
/**
 * #674 — the frame every content title is drawn in, as CSS. Mirrors
 * `TITLE_FRAME_STROKE / _INSET / _RADIUS` in `template.typ` (0.8pt / 6pt / 2pt),
 * so the preview shows the same framed, bold title the PDF prints.
 */
exports.TITLE_FRAME_CSS = {
    borderWidth: '1px',
    padding: '8px',
    borderRadius: '3px',
    fontWeight: 700,
};
/** Defaults equal to today's hardcoded title styling — keep in sync with template.typ. */
exports.TITLE_STYLE_DEFAULTS = {
    color: 'red',
    align: 'center',
    size: 'medium',
};
exports.titleColorKeySchema = zod_1.z.enum(Object.keys(exports.TITLE_COLORS));
exports.titleAlignKeySchema = zod_1.z.enum(Object.keys(exports.TITLE_ALIGNS));
exports.titleSizeKeySchema = zod_1.z.enum(Object.keys(exports.TITLE_SIZES));
const isPresetKey = (presets, key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(presets, key);
/** Resolve a stored color key (or null/unknown) to a Typst fill token, default-safe. */
const resolveTitleColor = (key) => exports.TITLE_COLORS[isPresetKey(exports.TITLE_COLORS, key) ? key : exports.TITLE_STYLE_DEFAULTS.color].typst;
exports.resolveTitleColor = resolveTitleColor;
/** Resolve a stored alignment key (or null/unknown) to a Typst align token, default-safe. */
const resolveTitleAlign = (key) => exports.TITLE_ALIGNS[isPresetKey(exports.TITLE_ALIGNS, key) ? key : exports.TITLE_STYLE_DEFAULTS.align].typst;
exports.resolveTitleAlign = resolveTitleAlign;
/** Resolve a stored size key (or null/unknown) to a Typst size token, default-safe. */
const resolveTitleSize = (key) => exports.TITLE_SIZES[isPresetKey(exports.TITLE_SIZES, key) ? key : exports.TITLE_STYLE_DEFAULTS.size].typst;
exports.resolveTitleSize = resolveTitleSize;
/** Resolve a stored color key to a CSS colour, default-safe (#677 preview). */
const resolveTitleColorCss = (key) => exports.TITLE_COLORS[isPresetKey(exports.TITLE_COLORS, key) ? key : exports.TITLE_STYLE_DEFAULTS.color].css;
exports.resolveTitleColorCss = resolveTitleColorCss;
/** Resolve a stored alignment key to a CSS `text-align`, default-safe (#677 preview). */
const resolveTitleAlignCss = (key) => exports.TITLE_ALIGNS[isPresetKey(exports.TITLE_ALIGNS, key) ? key : exports.TITLE_STYLE_DEFAULTS.align].css;
exports.resolveTitleAlignCss = resolveTitleAlignCss;
/** Resolve a stored size key to a CSS `font-size`, default-safe (#677 preview). */
const resolveTitleSizeCss = (key) => exports.TITLE_SIZES[isPresetKey(exports.TITLE_SIZES, key) ? key : exports.TITLE_STYLE_DEFAULTS.size].css;
exports.resolveTitleSizeCss = resolveTitleSizeCss;
/**
 * The whole framed title as CSS properties — colour, alignment and size from the
 * stored presets, frame and bold weight from the fixed #674 look. One call so a
 * preview cannot render half the styling (#677).
 */
const resolveTitleStyleCss = (style) => {
    const color = (0, exports.resolveTitleColorCss)(style.titleColor);
    return {
        color,
        textAlign: (0, exports.resolveTitleAlignCss)(style.titleAlign),
        fontSize: (0, exports.resolveTitleSizeCss)(style.titleSize),
        fontWeight: exports.TITLE_FRAME_CSS.fontWeight,
        border: `${exports.TITLE_FRAME_CSS.borderWidth} solid ${color}`,
        borderRadius: exports.TITLE_FRAME_CSS.borderRadius,
        padding: exports.TITLE_FRAME_CSS.padding,
    };
};
exports.resolveTitleStyleCss = resolveTitleStyleCss;
/** `{ value, label }` option lists for Ant Design `Select` controls. */
exports.TITLE_COLOR_OPTIONS = Object.keys(exports.TITLE_COLORS).map((value) => ({
    value,
    label: exports.TITLE_COLORS[value].label,
}));
exports.TITLE_ALIGN_OPTIONS = Object.keys(exports.TITLE_ALIGNS).map((value) => ({
    value,
    label: exports.TITLE_ALIGNS[value].label,
}));
exports.TITLE_SIZE_OPTIONS = Object.keys(exports.TITLE_SIZES).map((value) => ({
    value,
    label: exports.TITLE_SIZES[value].label,
}));
//# sourceMappingURL=urbanTitleStyle.js.map